/**
 * localized-text — the trigger a copy field wears when its text carries (or
 * will carry) versions in the shop's other languages: one quiet button that
 * opens `<localized-text-editor>` over the field, and re-emits what the
 * operator did in there as a single bubbling event — `save` for a corrected
 * map, `translate` for a fresh pass over the same words.
 *
 * ONE CONTROL OUTSIDE, THE SECOND ACTION WHERE IT IS JUDGED. Seeing the
 * versions and asking for new ones are two things, and only the first is a
 * door: the button opens the versions, and the ask lives in the overlay's
 * own footer beside the panes it would replace, which is the only place an
 * operator can tell whether it is worth asking. Two buttons out here would
 * put the destructive one in front of every operator who only wanted to
 * read.
 *
 * THE DOOR IS ALWAYS THERE, EVEN WITH NOTHING BEHIND IT. A field whose
 * source is not stored yet has no versions to read and nothing to translate,
 * so the trigger renders disabled — never absent, which is how an operator
 * only finds the control after the thing it controls has already happened.
 * `nothingToLocalize` is that rule's one declaration; `<localize-notice>`
 * reads the same call to say why under the field, because this element now
 * lives inside the field's `actions` slot, where a sentence has no room and
 * on a single-line field no box at all.
 *
 * A CONTROL, AND NOTHING ELSE. `actions` is a control slot: on an input it is
 * an absolutely-positioned band at the trailing edge whose measured width the
 * field gives up from its own text inset, so anything that is not a compact
 * control either overflows the box or eats the value. This element therefore
 * renders one button and `display: contents`, which puts that button in the
 * slot directly rather than a host box around it.
 *
 * PROPERTY-DRIVEN ON PURPOSE. The three fields that carry a localized map —
 * an event's description, a ticket type's name and description — live in different
 * states and persist down different channels, so the mounting form hands this
 * trigger the resolved facts (label, stored source, stored map, whether a
 * background pass is writing right now) and routes the events to its own
 * write path. An empty corrected map comes back as `byLocale: null`,
 * because a map is cleared by writing the whole field null — an entry is a
 * string or it is not there. Zero I/O here.
 */
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { show } from "@mhmo91/schmancy/overlay";
import type { LocalizedText } from "@momo/i18n";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { createRef, ref } from "lit/directives/ref.js";
import { when } from "lit/directives/when.js";
import { takeUntil } from "rxjs";
import { LocalizeMixin } from "../../i18n/localize.ts";
import type { LocalizedTextEditor } from "./localized-text-editor.view.ts";
import "./localized-text-editor.view.ts";

export type LocalizedTextSaveEvent = CustomEvent<{
	byLocale: LocalizedText | null;
	settle: (accepted: boolean) => void;
}>;

export type LocalizedTextTranslateEvent = CustomEvent<{
	settle: (accepted: boolean) => void;
}>;

/**
 * A field nobody has saved words into has nothing to show and nothing to
 * translate — the versions are written from the text as it stands ON the
 * record, never from the form's uncommitted draft. Declared once and read
 * twice: by the trigger, which disables itself, and by `<localize-notice>`,
 * which says why under the field.
 */
export const nothingToLocalize = (source: string): boolean =>
	source.trim().length === 0;

@schmancyElement("localized-text")
export class LocalizedTextTrigger extends LocalizeMixin(SchmancyElement) {
	// The button is what the field slots, not a box around it: the input's
	// actions band is absolutely positioned and its measured width comes off
	// the field's own text inset, so an interposed host would reserve room the
	// value needs and take the band's `items-center` for itself.
	static styles = [
		css`
			:host {
				display: contents;
			}
		`,
	];

	/** The field's own label, as the form shows it ("Description"). */
	@property() label = "";
	/** The STORED source string — never the form's uncommitted draft, so a
	 *  correction is always read against words the storefront actually serves. */
	@property() source = "";
	@property({ attribute: false }) byLocale: LocalizedText | null = null;
	/** A pass is asked for or writing right now — both read as one spinner,
	 *  because an ask sits at `requested` until the trigger picks it up and an
	 *  operator watching the button cannot tell the two apart. */
	@property({ type: Boolean }) pending = false;
	/** Prose by default; false for a single-line field (a ticket type's name). */
	@property({ type: Boolean }) multiline = true;
	/** Per-language cap, where the field carries one. */
	@property({ type: Number }) maxlength?: number;
	/** Locked by the mounting form for a reason of its own — an unsaved edit
	 *  to the very string a pass would read — which that form states itself. */
	@property({ type: Boolean }) disabled = false;
	/** Set by the field this trigger sits in, off the FIELD's own box rather
	 *  than the viewport, and forwarded to the button because the button is the
	 *  element that knows how to collapse: `<schmancy-button compact>` drops to
	 *  the rung's square and clips its label to the accessibility layer, so the
	 *  collapsed control keeps the name it had. Without this hop the attribute
	 *  lands on a host that renders nothing and the control never collapses. */
	@property({ type: Boolean }) compact = false;

	/** The mounted overlay editor, while one is open. The overlay re-invokes
	 *  its factory only on signal changes, so a background write landing as
	 *  fresh PROPERTIES on this trigger (pending → done, the map arriving)
	 *  would never reach an already-open editor — `updated()` forwards them
	 *  through the editor's own public API instead, which is what lets the
	 *  skeleton fill in place the moment the pass lands. */
	private readonly editorRef = createRef<LocalizedTextEditor>();

	protected override updated() {
		// The field warns in DEV about a slotted action it can read no name off,
		// and this host is all it can read: the name itself sits on the button a
		// shadow root down. `display: contents` leaves the host the `generic`
		// role, which prohibits a name, so answering the check costs no second
		// announcement.
		this.setAttribute(
			"aria-label",
			this.t("admin.events.localize.triggerAriaLabel", { field: this.label }),
		);
		const editor = this.editorRef.value;
		if (!editor) return;
		editor.byLocale = this.byLocale;
		editor.pending = this.pending;
	}

	private readonly open = () => {
		show(
			() => html`
				<localized-text-editor
					${ref(this.editorRef)}
					.label=${this.label}
					.source=${this.source}
					.byLocale=${this.byLocale}
					.pending=${this.pending}
					.multiline=${this.multiline}
					maxlength=${ifDefined(this.maxlength)}
					@save=${(event: LocalizedTextSaveEvent) => {
						event.stopPropagation();
						this.dispatchEvent(
							new CustomEvent("save", {
								detail: event.detail,
								bubbles: true,
								composed: true,
							}),
						);
					}}
					@translate=${(event: LocalizedTextTranslateEvent) => {
						event.stopPropagation();
						this.dispatchEvent(
							new CustomEvent("translate", {
								detail: event.detail,
								bubbles: true,
								composed: true,
							}),
						);
					}}
				></localized-text-editor>
			`,
			{
				ariaLabel: this.t("admin.events.localize.triggerAriaLabel", {
					field: this.label,
				}),
			},
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe();
	};

	override render() {
		return html`
			<schmancy-button
				variant="text"
				size="sm"
				?compact=${this.compact}
				?disabled=${this.disabled || nothingToLocalize(this.source)}
				aria-label=${this.t("admin.events.localize.triggerAriaLabel", {
					field: this.label,
				})}
				@click=${this.open}
			>
				${when(
					this.pending,
					() =>
						html`<schmancy-spinner slot="prefix" size="sm"></schmancy-spinner>`,
					() =>
						html`<schmancy-icon slot="prefix" aria-hidden="true">translate</schmancy-icon>`,
				)}
				${this.t("admin.events.localize.openButton")}
			</schmancy-button>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"localized-text": LocalizedTextTrigger;
	}
}
